class CollectionProductCard extends HTMLElement {
  constructor() {
    super();

    this.variantData = this.getVariantData();
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('input[name="id"]');
    this.addButton = this.querySelector('[data-collection-add]');
    this.addButtonText = this.querySelector('[data-add-button-text]');
    this.buyButton = this.querySelector('[data-collection-buy]');
    this.optionInputs = this.querySelectorAll('[data-product-option]');
    this.swatchLabels = this.querySelectorAll('[data-swatch-label]');

    this.selectedVariant = null;
    this.imageTimer = null;

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.updateSelectedVariant();
      });
    });

    this.swatchLabels.forEach((swatch) => {
      swatch.addEventListener('mouseenter', () => {
        this.previewSwatchVariant(swatch);
      });

      swatch.addEventListener('mouseleave', () => {
        this.restoreSelectedVariantImage();
      });

      swatch.addEventListener('focusin', () => {
        this.previewSwatchVariant(swatch);
      });

      swatch.addEventListener('focusout', () => {
        this.restoreSelectedVariantImage();
      });
    });

    this.updateSelectedVariant();
  }

  getVariantData() {
    const jsonElement = this.querySelector(
      'script[type="application/json"][data-variant-json]'
    );

    if (!jsonElement) return [];

    try {
      return JSON.parse(jsonElement.textContent);
    } catch (error) {
      console.error('Could not read product variants:', error);
      return [];
    }
  }

  getSelectedOptions() {
    const optionGroups = this.querySelectorAll('[data-option-group]');

    return Array.from(optionGroups).map((group) => {
      const checkedInput = group.querySelector(
        'input[data-product-option]:checked'
      );

      const selectInput = group.querySelector(
        'select[data-product-option]'
      );

      if (checkedInput) return checkedInput.value;
      if (selectInput) return selectInput.value;

      return null;
    });
  }

  findVariantForOptions(options) {
    return this.variantData.find((variant) => {
      return variant.options.every((option, index) => {
        return option === options[index];
      });
    });
  }

  updateSelectedVariant() {
    const selectedOptions = this.getSelectedOptions();
    const selectedVariant = this.findVariantForOptions(selectedOptions);

    if (!selectedVariant) {
      this.selectedVariant = null;
      this.disableButtons('Unavailable');
      this.updateSelectedSwatches();
      this.updateOptionLabels();
      return;
    }

    this.selectedVariant = selectedVariant;

    if (this.variantInput) {
      this.variantInput.value = selectedVariant.id;
      this.variantInput.disabled = false;
    }

    this.updateButtons(selectedVariant);
    this.updateSelectedSwatches();
    this.updateOptionLabels();
    this.updateProductImage(selectedVariant);
  }

  updateButtons(variant) {
    if (!variant.available) {
      this.disableButtons('Sold out');
      return;
    }

    if (this.addButton) {
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-disabled');
    }

    if (this.buyButton) {
      this.buyButton.disabled = false;
      this.buyButton.removeAttribute('aria-disabled');
      this.buyButton.dataset.variantId = variant.id;
    }

    if (this.addButtonText) {
      this.addButtonText.textContent = 'Add to cart';
    }
  }

  disableButtons(message) {
    if (this.addButton) {
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-disabled', 'true');
    }

    if (this.buyButton) {
      this.buyButton.disabled = true;
      this.buyButton.setAttribute('aria-disabled', 'true');
      delete this.buyButton.dataset.variantId;
    }

    if (this.addButtonText) {
      this.addButtonText.textContent = message;
    }
  }

  updateSelectedSwatches() {
    this.querySelectorAll('.collection-card-swatch').forEach((swatch) => {
      const input = swatch.querySelector('input');

      swatch.classList.toggle(
        'is-selected',
        Boolean(input && input.checked)
      );
    });
  }

  updateOptionLabels() {
    this.querySelectorAll('[data-option-group]').forEach((group) => {
      const selectedValueLabel = group.querySelector(
        '[data-selected-option-value]'
      );

      if (!selectedValueLabel) return;

      const checkedInput = group.querySelector(
        'input[data-product-option]:checked'
      );

      const selectInput = group.querySelector(
        'select[data-product-option]'
      );

      if (checkedInput) {
        selectedValueLabel.textContent = checkedInput.value;
      } else if (selectInput) {
        selectedValueLabel.textContent = selectInput.value;
      }
    });
  }

  previewSwatchVariant(swatch) {
    if (swatch.classList.contains('is-disabled')) return;

    const optionPosition = Number(swatch.dataset.optionPosition);
    const optionValue = swatch.dataset.optionValue;

    if (!optionPosition || !optionValue) return;

    const previewOptions = this.getSelectedOptions();
    previewOptions[optionPosition - 1] = optionValue;

    let previewVariant = this.findVariantForOptions(previewOptions);

    /*
     * If another option combination prevents an exact match,
     * preview the first variant containing this color.
     */
    if (!previewVariant) {
      previewVariant = this.variantData.find((variant) => {
        return variant.options[optionPosition - 1] === optionValue;
      });
    }

    if (!previewVariant || !previewVariant.featured_image) return;

    this.updateProductImage(previewVariant, true);
  }

  restoreSelectedVariantImage() {
    if (!this.selectedVariant) return;

    this.updateProductImage(this.selectedVariant, true);
  }

  updateProductImage(variant, isPreview = false) {
    if (!variant || !variant.featured_image) return;

    const productCard = this.closest('.product-card-wrapper');
    if (!productCard) return;

    productCard.classList.add('variant-image-selected');

    const mainImage = productCard.querySelector(
      '.card__media img:first-child'
    );

    if (!mainImage) return;

    const imageUrl =
      variant.featured_image.src ||
      variant.featured_image.url;

    if (!imageUrl) return;

    const separator = imageUrl.includes('?') ? '&' : '?';

    const newSrc = `${imageUrl}${separator}width=720`;

    const newSrcset = [
      `${imageUrl}${separator}width=165 165w`,
      `${imageUrl}${separator}width=360 360w`,
      `${imageUrl}${separator}width=533 533w`,
      `${imageUrl}${separator}width=720 720w`
    ].join(', ');

    /*
     * Avoid restarting the animation when the image is already active.
     */
    if (mainImage.src === newSrc || mainImage.currentSrc === newSrc) {
      return;
    }

    window.clearTimeout(this.imageTimer);

    mainImage.classList.add('collection-card-image--changing');

    this.imageTimer = window.setTimeout(() => {
      mainImage.src = newSrc;
      mainImage.srcset = newSrcset;

      mainImage.alt =
        variant.featured_image.alt ||
        variant.title ||
        'Product image';

      /*
       * Keep preview selections temporary. The actual variant ID changes
       * only when the customer clicks a swatch.
       */
      if (!isPreview && this.variantInput) {
        this.variantInput.value = variant.id;
      }

      requestAnimationFrame(() => {
        mainImage.classList.remove('collection-card-image--changing');
      });
    }, 90);
  }
}

if (!customElements.get('collection-product-card')) {
  customElements.define(
    'collection-product-card',
    CollectionProductCard
  );
}

document.addEventListener('click', (event) => {
  const buyButton = event.target.closest('[data-collection-buy]');

  if (!buyButton || buyButton.disabled) return;

  event.preventDefault();
  event.stopPropagation();

  const variantId = buyButton.dataset.variantId;

  if (!variantId) return;

  const root = window.Shopify?.routes?.root || '/';

  window.location.href = `${root}cart/${variantId}:1`;
});